import React, { useEffect, useRef, useState, useCallback } from "react";
import { Heart, Hand, Gem, ThumbsUp } from "lucide-react";

/**
 * LiveReactions — overlay + bar for live reactions.
 * Renders a fixed layer inside the parent (absolute) that spawns floating emojis
 * whenever the polled `reactions` list has new items, and shows a bar of
 * clickable buttons at the bottom for the viewer/broadcaster to react.
 */

const REACTION_META = {
  gem:   { emoji: "💎", label: "Joia",   color: "#67e8f9", Icon: Gem },
  heart: { emoji: "❤️", label: "Coração", color: "#ef4444", Icon: Heart },
  clap:  { emoji: "👏", label: "Palmas", color: "#facc15", Icon: Hand },
  like:  { emoji: "👍", label: "Curtir", color: "#3b82f6", Icon: ThumbsUp },
};

// Absolute overlay that shows floating emojis. Feed with `pushLocal(type)`.
export function LiveReactionsLayer({ reactions = [], totals = {}, testIdPrefix = "live" }) {
  // items to animate: {key, type, x}
  const [floaters, setFloaters] = useState([]);
  const seenRef = useRef(new Set());
  const keyRef = useRef(0);

  // spawn floaters for any new reaction id we haven't seen yet
  useEffect(() => {
    if (!reactions?.length) return;
    const fresh = [];
    for (const r of reactions) {
      const id = r.r_id || `${r.user_id}-${r.created_at}`;
      if (seenRef.current.has(id)) continue;
      seenRef.current.add(id);
      // seed if we're just mounting to avoid a burst of stale reactions
      if (seenRef.current.size <= reactions.length && seenRef.current.size > 5) continue;
      fresh.push(r);
    }
    if (!fresh.length) return;
    setFloaters((prev) => {
      const nxt = [...prev];
      for (const r of fresh) {
        keyRef.current += 1;
        nxt.push({ key: keyRef.current, type: r.type, x: 20 + Math.random() * 60 });
      }
      // cap
      return nxt.slice(-40);
    });
  }, [reactions]);

  // cleanup finished floaters
  useEffect(() => {
    if (!floaters.length) return;
    const t = setTimeout(() => setFloaters((f) => f.slice(1)), 3500);
    return () => clearTimeout(t);
  }, [floaters]);

  const push = useCallback((type) => {
    keyRef.current += 1;
    setFloaters((f) => [...f, { key: keyRef.current, type, x: 20 + Math.random() * 60 }].slice(-40));
  }, []);

  const totalsSummary = ["heart", "clap", "gem", "like"]
    .map((k) => ({ k, v: totals[k] || 0 }))
    .filter((x) => x.v > 0);

  return {
    push,
    // renderable overlay + totals badge
    overlay: (
      <>
        <div className="pointer-events-none absolute inset-0 overflow-hidden" data-testid={`${testIdPrefix}-reactions-layer`}>
          {floaters.map((f) => {
            const meta = REACTION_META[f.type] || REACTION_META.like;
            return (
              <span
                key={f.key}
                className="absolute bottom-4 text-3xl live-float-up select-none"
                style={{ left: `${f.x}%` }}
              >
                {meta.emoji}
              </span>
            );
          })}
        </div>
        {totalsSummary.length > 0 && (
          <div className="absolute top-3 right-3 flex gap-1 z-10" data-testid={`${testIdPrefix}-reactions-totals`}>
            {totalsSummary.map(({ k, v }) => (
              <div
                key={k}
                className="px-2 py-0.5 rounded-full bg-black/60 backdrop-blur-sm text-white text-xs font-semibold flex items-center gap-1"
                data-testid={`${testIdPrefix}-total-${k}`}
              >
                <span>{REACTION_META[k].emoji}</span>
                <span>{v > 999 ? `${(v / 1000).toFixed(1)}k` : v}</span>
              </div>
            ))}
          </div>
        )}
      </>
    ),
  };
}

// Bottom bar of reaction buttons
export function LiveReactionsBar({ onReact, testIdPrefix = "live" }) {
  return (
    <div className="flex items-center justify-center gap-2 p-3 bg-black/70 backdrop-blur-sm" data-testid={`${testIdPrefix}-reactions-bar`}>
      {(["gem", "heart", "clap", "like"]).map((t) => {
        const meta = REACTION_META[t];
        return (
          <button
            key={t}
            data-testid={`${testIdPrefix}-react-${t}`}
            onClick={() => onReact?.(t)}
            aria-label={meta.label}
            className="w-11 h-11 rounded-full bg-white/10 hover:bg-white/25 border border-white/20 text-white flex items-center justify-center text-xl transition-all active:scale-90 hover:scale-110"
          >
            {meta.emoji}
          </button>
        );
      })}
    </div>
  );
}

export { REACTION_META };
